/**
 * Exit poll (survey team, polling day): one voter at a time, big buttons — whom they voted for, age group,
 * gender, (optional) last time's vote and main issue. No name, no phone. Saved on the phone at once and
 * sent when the network allows; the war room checks doubtful answers.
 */
import { useEffect, useMemo, useState } from "react";
import { apiErrorText } from "@core/messages";
import type { ExitAnswer, ExitPlanInfo } from "../field/api";
import { ExitQueue } from "../field/exitpoll";
import { makeId, prefs } from "../platform/browser";
import { useApp, useEmitter, useQuery, useSession } from "../ui/context";
import { Banner, Button } from "../ui/components";

const AGES = ["a18_25", "a26_40", "a41_60", "a60plus"] as const;

export function ExitPollScreen() {
  const { app, t, lang } = useApp();
  const { assemblyId } = useSession();
  const queue = useMemo(
    () => new ExitQueue({ api: app.api, assemblyId: () => app.assemblyId, kv: prefs, online: () => navigator.onLine !== false, onUnauthenticated: (e) => void app.core.expireSession(e as never) }),
    [app]
  );
  useEmitter(queue.changes);
  const booths = useQuery(async () => (assemblyId ? app.core.store.listBooths(assemblyId) : []), [assemblyId], ["booths"]);
  const [plan, setPlan] = useState<ExitPlanInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mine, setMine] = useState<{ today: number; review: number } | null>(null);
  const [booth, setBooth] = useState(() => prefs.get("exit.booth") ?? "");
  const [method, setMethod] = useState<"face" | "phone">(() => (prefs.get("exit.method") as "face" | "phone") ?? "face");
  const [f, setF] = useState<{ choice: string; age: string; gender: string; prev: string; issue: string }>({ choice: "", age: "", gender: "", prev: "", issue: "" });
  const [more, setMore] = useState(false);
  const [saved, setSaved] = useState(0);
  const [fix, setFix] = useState<{ lat: number; lng: number; acc: number } | null>(null);

  useEffect(() => {
    let off = false;
    app.online((api, aid) => api.exitPlan(aid)).then(
      (r) => !off && (setPlan(r.plan), prefs.set("exit.plan", JSON.stringify(r.plan))),
      (e) => {
        if (off) return;
        // offline: the plan from last time
        const cached = prefs.get("exit.plan");
        if (cached) setPlan(JSON.parse(cached) as ExitPlanInfo);
        else setErr(apiErrorText(e, lang) ?? t.resNeedsNet);
      }
    );
    const load = () => app.online((api, aid) => api.exitMine(aid)).then((m) => !off && setMine(m), () => undefined);
    void load();
    const h = setInterval(() => {
      void queue.flush();
      void load();
    }, 30_000);
    const on = () => void queue.flush();
    window.addEventListener("online", on);
    // a GPS fix kept fresh while the screen is open
    let watch: number | null = null;
    if (navigator.geolocation)
      watch = navigator.geolocation.watchPosition((p) => setFix({ lat: p.coords.latitude, lng: p.coords.longitude, acc: Math.round(p.coords.accuracy) }), () => undefined, { enableHighAccuracy: true, maximumAge: 60_000, timeout: 30_000 });
    return () => {
      off = true;
      clearInterval(h);
      window.removeEventListener("online", on);
      if (watch != null) navigator.geolocation.clearWatch(watch);
    };
  }, [app, queue, lang, t]);

  const ready = !!booth && !!f.choice && !!f.age && !!f.gender;
  const save = () => {
    if (!ready || !plan) return;
    const a: ExitAnswer = {
      clientId: makeId(), boothCode: booth, collectedAt: new Date().toISOString(), choice: f.choice, prevChoice: f.prev || null,
      ageGroup: f.age as ExitAnswer["ageGroup"], gender: f.gender as ExitAnswer["gender"], issue: f.issue || null, method,
      lat: fix ? Math.round(fix.lat * 1e6) / 1e6 : null, lng: fix ? Math.round(fix.lng * 1e6) / 1e6 : null, accuracyM: fix?.acc ?? null,
    };
    queue.add(a);
    setSaved((n) => n + 1);
    setF({ choice: "", age: "", gender: "", prev: "", issue: "" });
    window.scrollTo(0, 0);
  };
  const pick = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: x[k] === v ? "" : v }));
  const q = queue.queued.length;

  return (
    <div className="stack" data-testid="exitpoll">
      <h2 style={{ margin: 0 }}>📊 {t.epTitle}</h2>
      <Banner kind="warn">{t.epRules}</Banner>
      {err ? <Banner kind="error">{err}</Banner> : null}
      <div className="ep-count">
        <span>
          {t.epSavedNow(saved)} · {t.epToday(mine?.today ?? 0)}
        </span>
        <span className={q ? "warn" : "ok"}>{q ? t.epQueued(q) : t.epAllSent}</span>
      </div>
      {plan && !plan.saved ? <Banner kind="info">{t.epNoPlan}</Banner> : null}

      <label className="label" htmlFor="ep-booth">
        {t.epBooth}
      </label>
      <select id="ep-booth" className="input" value={booth} onChange={(e) => { setBooth(e.target.value); prefs.set("exit.booth", e.target.value || null); }}>
        <option value="">{t.epPickBooth}</option>
        {(booths ?? []).map((b) => (
          <option key={b.code} value={b.code}>
            {b.code} · {b.name}
          </option>
        ))}
      </select>

      {plan ? (
        <>
          <div className="label">{t.epChoice}</div>
          <div className="ep-btns big">
            {plan.options.map((o) => (
              <button key={o.key} type="button" className={f.choice === o.key ? "on" : undefined} onClick={() => pick("choice", o.key)} data-testid={`ep-choice-${o.key}`}>
                {o.label}
              </button>
            ))}
          </div>
          <div className="label">{t.epAge}</div>
          <div className="ep-btns">
            {AGES.map((a) => (
              <button key={a} type="button" className={f.age === a ? "on" : undefined} onClick={() => pick("age", a)}>
                {t.epAges[a]}
              </button>
            ))}
          </div>
          <div className="label">{t.epGender}</div>
          <div className="ep-btns">
            {(["M", "F", "O"] as const).map((g) => (
              <button key={g} type="button" className={f.gender === g ? "on" : undefined} onClick={() => pick("gender", g)}>
                {t.epGenders[g]}
              </button>
            ))}
          </div>
          <button type="button" className="linklike" onClick={() => setMore((x) => !x)}>
            {more ? "▾" : "▸"} {t.epMore}
          </button>
          {more ? (
            <>
              <div className="label">{t.epPrev}</div>
              <div className="ep-btns">
                {[...plan.options, { key: "first_time", label: t.epFirstTime }].map((o) => (
                  <button key={o.key} type="button" className={f.prev === o.key ? "on" : undefined} onClick={() => pick("prev", o.key)}>
                    {o.label}
                  </button>
                ))}
              </div>
              {plan.issues.length ? (
                <>
                  <div className="label">{t.epIssue}</div>
                  <div className="ep-btns">
                    {plan.issues.map((i) => (
                      <button key={i} type="button" className={f.issue === i ? "on" : undefined} onClick={() => pick("issue", i)}>
                        {i}
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
              <div className="label">{t.epMethod}</div>
              <div className="ep-btns">
                {(["face", "phone"] as const).map((m) => (
                  <button key={m} type="button" className={method === m ? "on" : undefined} onClick={() => { setMethod(m); prefs.set("exit.method", m); }}>
                    {t.epMethods[m]}
                  </button>
                ))}
              </div>
            </>
          ) : null}
          <Button big onClick={save} disabled={!ready} testId="ep-save">
            {t.epSave}
          </Button>
          {!booth ? <p className="muted">{t.epNeedBooth}</p> : null}
          <p className="muted" style={{ fontSize: 13 }}>{fix ? t.epGps(fix.acc) : t.epNoGps}</p>
        </>
      ) : !err ? (
        <p className="muted">…</p>
      ) : null}
    </div>
  );
}
